import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort } from 'types';
import { getRestaurantsByCategoryShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './index.module.scss';

interface IProps {
	setIDsRestaurant: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	IDSelectedCategory: Nullable<string>;
	IDsSelectedRestaurant: string[];
}

export const RestaurantsByCategoryMultipleSelect: React.FC<IProps> = React.memo(
	({ setIDsRestaurant, setFetchedRestaurants, IDSelectedCategory, IDsSelectedRestaurant }) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const hasAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);
		const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, string>(
			IDSelectedCategory ? getRestaurantsByCategoryShortInfo : null,
			IDSelectedCategory || '',
		);

		const restaurants = useMemo(() => {
			if (hasAccess && response) {
				return response.data.restaurants;
			}
			if (!hasAccess && response) {
				const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
				return response.data.restaurants.filter((restaurant) =>
					accessibleRestaurantIDs.includes(restaurant.id),
				);
			}
		}, [hasAccess, response, loggedUser]);

		const handleRemove = useCallback(
			(id: string) => {
				const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
				setIDsRestaurant(updatedSelectedItems);
			},
			[IDsSelectedRestaurant, setIDsRestaurant],
		);

		const restaurantsList = useMemo(() => {
			return IDsSelectedRestaurant.map((el) => {
				const label = restaurants?.find((item) => item.id === el)?.name || '';

				return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
			});
		}, [IDsSelectedRestaurant, restaurants, handleRemove]);

		useEffect(() => {
			refetch();
		}, [IDSelectedCategory]);

		useEffect(() => {
			if (response) {
				setFetchedRestaurants(response.data.restaurants);
			} else if (!hasAccess) {
				const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
				setFetchedRestaurants(accessibleRestaurants);
			}
		}, [response, loggedUser, hasAccess, IDSelectedCategory]);

		return (
			<div className={style.container} data-test="RestaurantsMultipleSelect">
				<div className={style.field}>
					<MultipleSelect
						data={restaurants || []}
						isLoading={loading}
						refetch={refetch}
						responseError={responseError}
						setIDsRestaurant={setIDsRestaurant}
						IDSelectedCustomer={IDSelectedCategory}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<div className={style.groupsList}>{restaurantsList}</div>
			</div>
		);
	},
);
